import React, { useEffect, useContext, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Typography, Grid, Form, Select, Input, theme, Upload, Spin, DatePicker, Tooltip, Flex } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import _ from 'lodash';
import dayjs from 'dayjs';
import { MessageContext } from '../../../layouts/NavigationLayout';
import Countries from '../../../constants/Countries';

//Constants
import CommonEnum from '../../../constants/CommonEnum';

//Helpers
import GeneralHelper from '../../../helpers/general';
import AssetHelper from '../../../helpers/asset';
import LabelHelper from '../../../helpers/label';

//Components
import Confirmation from '../../../components/Confirmation';
import ButtonGroupComponent from './ButtonGroupComponent';
import CustomItemRender from './CustomItemRender';

const { Text, Title } = Typography;
const { Item } = Form;
const { useBreakpoint } = Grid;
const { useToken } = theme;
const { Dragger } = Upload;
const { TextArea } = Input;

const ApplicationStatusView = (props) => {

	const [passportFiles, setPassportFiles] = useState([]);
	const [travelFiles, setTravelFiles] = useState([]);
	const [insuranceFiles, setInsuranceFiles] = useState([]);
	const [sendingTravelItinerary, setSendingTravelItinerary] = useState(false);
	const [sendingInsurance, setSendingInsurance] = useState(false);
	const [warningPhone, setWarningPhone] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [passportIsRequired, setPassportIsRequired] = useState(false);
	const [travelItineraryIsRequired, setTravelItineraryIsRequired] = useState(false);
	const [insuranceIsRequired, setInsuranceIsRequired] = useState(false);
	const [sendingPassport, setSendingPassport] = useState(false);
	const [confirmation, setConfirmation] = useState(false);
	const [successDisplayed, setSuccessDisplayed] = useState(false);
	const [specialDietReq, setSpecialDietReq] = useState(false);
	const [saveType, setSaveType] = useState(null);
	const [submitPartOneDisabled, setSubmitPartOneDisabled] = useState(true);
	const [submitPartTwoDisabled, setSubmitPartTwoDisabled] = useState(true);
	const [submitPartThreeDisabled, setSubmitPartThreeDisabled] = useState(true);
	const [submitPartFourDisabled, setSubmitPartFourDisabled] = useState(true);
	const [partOneFieldsDisabled, setPartOneFieldsDisabled] = useState(false);
	const [partTwoFieldsDisabled, setPartTwoFieldsDisabled] = useState(false);
	const [partThreeFieldsDisabled, setPartThreeFieldsDisabled] = useState(false);
	const [partFourFieldsDisabled, setPartFourFieldsDisabled] = useState(false);

	const partOneFormRef = useRef(null);
	const partTwoFormRef = useRef(null);
	const partThreeFormRef = useRef(null);
	const partFourFormRef = useRef(null);
	const history = useHistory();
	const screens = useBreakpoint();
	const { token } = useToken();
	const assetActionUrl = AssetHelper.getActionUrl();
	const assetHeader = AssetHelper.getHeader();
	const query = GeneralHelper.getParamsFromUrl();

	const { colorPrimary, colorLink } = token;
	const messageApi = useContext(MessageContext);

	const { applicationStatusPageProps, sendPassportForReview, sendItineraryForReview, sendInsuranceForReview, getApplicationStatus, onApplicationStatusSubmit, resetApplicationStatusPage, resetApplicationStatusMeta } = props;
	const { application, loading, loadingSubmit, loadingPassportSubmit, loadingItinerarySubmit, loadingInsuranceSubmit, meta } = applicationStatusPageProps || {};

	const {
		display_id,
		status,
		remarks,
		gender,
		first_letter_received_at,
		second_letter_received_at,
		passport_received_at,
		special_dietary_req,
		special_dietary_req_detail,
		passport_review_status,
		insurance_received_at,
		insurance_review_status,
		emergency_contact,
		personal_info,
		passport_asset,
		travel_asset,
		travel_arrival,
		travel_departure,
		insurance_asset,
		section_one_submission_date,
		section_one_submission_date_string,
		section_two_submission_date,
		section_two_submission_date_string,
		section_three_submission_date,
		section_three_submission_date_string,
		section_four_submission_date,
		section_four_submission_date_string
	} = application || {};

	const {
		gender: personalInfoGender,
		require_visa,
		passport_num,
		country_issue,
		expiry_date
	} = personal_info || {};

	const {
		last_name,
		first_name,
		phone,
		email,
		relationship_with_applicant
	} = emergency_contact || {};

	useEffect(() => {
		query.application_id && getApplicationStatus(query.application_id);
		return () => {
			resetApplicationStatusPage();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (application) {
			validateFormFields();
			application.status === 'draft' && history.push(`/application?step=${application.current_step}&application_id=${application._id}`);
			!_.isNil(special_dietary_req) && setSpecialDietReq(special_dietary_req);

			let passportUploadedFiles = [];
			let travelUploadedFiles = [];
			let insuranceUploadedFiles = [];
			if (!_.isEmpty(passport_asset)) {
				// eslint-disable-next-line array-callback-return
				passport_asset.map(passportFile => {
					passportUploadedFiles.push({
						name: passportFile.name,
						type: passportFile.type,
						created_at: passportFile.created_at,
						status: 'done'
					});
				});
			}
			if (!_.isEmpty(travel_asset)) {
				// eslint-disable-next-line array-callback-return
				travel_asset.map(travelFile => {
					travelUploadedFiles.push({
						name: travelFile.name,
						type: travelFile.type,
						created_at: travelFile.created_at,
						status: 'done'
					});
				});
			}
			if (!_.isEmpty(insurance_asset)) {
				// eslint-disable-next-line array-callback-return
				insurance_asset.map(insuranceFile => {
					insuranceUploadedFiles.push({
						name: insuranceFile.name,
						type: insuranceFile.type,
						created_at: insuranceFile.created_at,
						status: 'done'
					});
				});
			}

			!_.isEmpty(passportUploadedFiles) && setPassportFiles(passportUploadedFiles);
			!_.isEmpty(travelUploadedFiles) && setTravelFiles(travelUploadedFiles);
			!_.isEmpty(insuranceUploadedFiles) && setInsuranceFiles(insuranceUploadedFiles);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [application]);

	useEffect(() => {
		if (meta) {
			if (meta.code === 200) {
				if (sendingPassport) {
					setPassportFiles([]);
					setSendingPassport(false);
				}

				if (sendingTravelItinerary) {
					setTravelFiles([]);
					setSendingTravelItinerary(false);
				}

				if (sendingInsurance) {
					setInsuranceFiles([]);
					setSendingInsurance(false);
				}

				setSubmitPartOneDisabled(true);
				setSubmitPartTwoDisabled(true);
				setSubmitPartThreeDisabled(true);
				setSubmitPartFourDisabled(true);
				setPassportIsRequired(false);
				setSaveType(null);
				setWarningPhone(false);
				setIsInitialLoad(true);
				setConfirmation(false);
				if (!successDisplayed) {
					messageApi.open({
						type: 'success',
						content: `${meta.message}`,
						duration: 2.5
					});
					setSuccessDisplayed(true);
				} else {
					setSuccessDisplayed(false);
				}
				query.application_id && getApplicationStatus(query.application_id);
			} else {
				messageApi.open({
					type: 'error',
					content: `${meta.message}`,
					duration: 2.5
				});
			}
		}
		return () => {
			resetApplicationStatusMeta();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [meta]);

	const filterOption = (input, option) =>
		(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

	const renderOptionLabel = (label) => {
		if (screens.xs && label.length > 25) {
			return (
				<Tooltip
					autoAdjustOverflow={true}
					placement="top"
					title={label}
					key={label}
				>
					{GeneralHelper.truncateLongText(label, 25)}
				</Tooltip>
			);
		}
		return label;
	};

	useEffect(() => {
		checkPartTwoValues();
		validatePassportFiles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [passportFiles]);

	useEffect(() => {
		checkPartThreeValues();
		validateTravelFiles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [travelFiles]);

	useEffect(() => {
		checkPartFourValues();
		validateInsuranceFiles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [insuranceFiles]);

	//Start of functions
	const checkPartOneValues = () => {
		if (partOneFormRef.current) {
			if (
				partOneFormRef.current.getFieldValue('special_dietary_req') &&
				(partOneFormRef.current.getFieldValue('special_dietary_req') === 'yes' ? partOneFormRef.current.getFieldValue('special_dietary_req_detail') : true) &&
				partOneFormRef.current.getFieldValue('gender') &&
				partOneFormRef.current.getFieldValue('last_name') &&
				partOneFormRef.current.getFieldValue('first_name') &&
				partOneFormRef.current.getFieldValue('phone') &&
				partOneFormRef.current.getFieldValue('email') &&
				partOneFormRef.current.getFieldValue('relationship')
			) {
				if (isInitialLoad) {
					setSubmitPartOneDisabled(true);
				} else {
					setSubmitPartOneDisabled(false);
				}
			} else {
				setSubmitPartOneDisabled(true);
			}
		}
	};

	const allFilesUploaded = (files) =>
		!_.isEmpty(files) && files.every(f => f.status === 'done' && f.asset_id);

	const checkPartTwoValues = () => {
		if (partTwoFormRef.current) {
			if (
				allFilesUploaded(passportFiles) &&
				partTwoFormRef.current.getFieldValue('passport_number') &&
				partTwoFormRef.current.getFieldValue('passport_country_of_issue') &&
				partTwoFormRef.current.getFieldValue('passport_expiry_date')
			) {
				if (isInitialLoad) {
					setSubmitPartTwoDisabled(true);
				} else {
					setSubmitPartTwoDisabled(false);
				}
			} else {
				setSubmitPartTwoDisabled(true);
			}
		}
	};

	const checkPartThreeValues = () => {
		if (partThreeFormRef.current) {
			if (
				require_visa && require_visa === 'no' ?
					(allFilesUploaded(travelFiles) &&
						partThreeFormRef.current.getFieldValue('travel_arrival') &&
						partThreeFormRef.current.getFieldValue('travel_departure')
					) :
					(
						!_.isEmpty(travelFiles) ||
						partThreeFormRef.current.getFieldValue('travel_arrival') ||
						partThreeFormRef.current.getFieldValue('travel_departure')
					) ?
						(allFilesUploaded(travelFiles) &&
							partThreeFormRef.current.getFieldValue('travel_arrival') &&
							partThreeFormRef.current.getFieldValue('travel_departure')
						) :
						false
			) {
				if (isInitialLoad) {
					setSubmitPartThreeDisabled(true);
				} else {
					setSubmitPartThreeDisabled(false);
				}
			} else {
				setSubmitPartThreeDisabled(true);
			}
		}
	};


	const checkPartFourValues = () => {
		if (partFourFormRef.current) {
			if (allFilesUploaded(insuranceFiles)) {
				if (isInitialLoad) {
					setSubmitPartFourDisabled(true);
				} else {
					setSubmitPartFourDisabled(false);
				}
			} else {
				setSubmitPartFourDisabled(true);
			}
		}
	};


	const validatePassportFiles = () => {
		if (_.isEmpty(passportFiles)) {
			if (isInitialLoad) {
				setPassportIsRequired(false);
			} else {
				setPassportIsRequired(true);
			}
		} else {
			setPassportIsRequired(false);
		}
	};

	const validateTravelFiles = () => {
		if (_.isEmpty(travelFiles) && require_visa === 'no') {
			if (isInitialLoad) {
				setTravelItineraryIsRequired(false);
			} else {
				setTravelItineraryIsRequired(true);
			}
		} else {
			setTravelItineraryIsRequired(false);
		}
	};

	const validateInsuranceFiles = () => {
		if (_.isEmpty(insuranceFiles)) {
			if (isInitialLoad) {
				setInsuranceIsRequired(false);
			} else {
				setInsuranceIsRequired(true);
			}
		} else {
			setInsuranceIsRequired(false);
		}
	};

	const validateEmail = (_, value) => {
		if (value && !/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/i.test(value)) {
			setSubmitPartOneDisabled(true);
			return Promise.reject('Please enter a valid email address');
		}
		return Promise.resolve();
	};

	const onPhoneNumberPaste = (event) => {
		const pastedText = event.clipboardData.getData('text');
		if (!/[0-9\s()+-]/.test(pastedText)) {
			event.preventDefault();
			setWarningPhone(true);
			setTimeout(() => {
				setWarningPhone(false);
			}, 3000);
		} else {
			setWarningPhone(false);
		}
	};
	const onPartOneSubmit = (values) => {
		const {
			phone,
			relationship,
			special_dietary_req,
			special_dietary_req_detail,
			email,
			first_name,
			gender,
			last_name
		} = values;

		const fields = {
			save_type: saveType,
			phone,
			relationship,
			special_dietary_req,
			special_dietary_req_detail,
			email,
			first_name,
			last_name,
			gender
		};

		if (application) {
			onApplicationStatusSubmit({ id: application._id, fields });
		}
	};

	const onPartTwoSubmit = (values) => {
		const {
			passport_number,
			passport_country_of_issue,
			passport_expiry_date
		} = values;

		const passportAsset = !_.isEmpty(passportFiles) ? passportFiles.map((passportFile, index) => {
			const suffix = index === 0 ? '' : index + 1;
			const fileExtension = passportFile.name.split('.').pop();
			return {
				name: personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Passport${suffix}.${fileExtension}` : passportFile.name,
				type: passportFile.type,
				created_at: passportFile.created_at
			};
		}) : [];

		const fields = {
			save_type: saveType,
			passport_number,
			passport_country_of_issue,
			passport_expiry_date,
			passport_asset: passportAsset
		};

		if (application) {
			if (!_.isEmpty(passportFiles)) {
				setSendingPassport(true);
				const fields = {
					application_id: query.application_id,
					assets: passportFiles.map(passportFile => passportFile.asset_id)
				};
				sendPassportForReview(fields);
			}
			onApplicationStatusSubmit({ id: application._id, fields });
		}
	};

	const onPartThreeSubmit = (values) => {
		const {
			travel_arrival,
			travel_departure
		} = values;

		const travelAsset = !_.isEmpty(travelFiles) ? travelFiles.map((travelFile, index) => {
			const suffix = index === 0 ? '' : index + 1;
			const fileExtension = travelFile.name.split('.').pop();
			return {
				name: personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Travel${suffix}.${fileExtension}` : travelFile.name,
				type: travelFile.type,
				created_at: travelFile.created_at
			};
		}) : [];

		const fields = {
			save_type: saveType,
			travel_arrival,
			travel_departure,
			travel_asset: travelAsset
		};

		if (application) {
			if (!_.isEmpty(travelFiles)) {
				setSendingTravelItinerary(true);
				const fields = {
					application_id: query.application_id,
					assets: travelFiles.map(travelFile => travelFile.asset_id)
				};
				sendItineraryForReview(fields);
			}
			onApplicationStatusSubmit({ id: application._id, fields });
		}
	};

	const onPartFourSubmit = (values) => {

		const insuranceAsset = !_.isEmpty(insuranceFiles) ? insuranceFiles.map((insuranceFile, index) => {
			const suffix = index === 0 ? '' : index + 1;
			const fileExtension = insuranceFile.name.split('.').pop();
			return {
				name: personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Insurance${suffix}.${fileExtension}` : insuranceFile.name,
				type: insuranceFile.type,
				created_at: insuranceFile.created_at
			};
		}) : [];

		const fields = {
			save_type: saveType,
			insurance_asset: insuranceAsset
		};

		if (application) {
			if (!_.isEmpty(insuranceFiles)) {
				setSendingInsurance(true);
				const fields = {
					application_id: query.application_id,
					assets: insuranceFiles.map(insuranceFile => insuranceFile.asset_id)
				};
				sendInsuranceForReview(fields);
			}
			onApplicationStatusSubmit({ id: application._id, fields });
		}
	};

	const validateFormFields = () => {
		const today = dayjs();
		if (
			today.isAfter(dayjs(section_one_submission_date).endOf('day')) ||
			gender ||
			special_dietary_req ||
			special_dietary_req_detail ||
			last_name ||
			first_name ||
			phone ||
			email ||
			relationship_with_applicant
		) {
			setPartOneFieldsDisabled(true);
		} else {
			setPartOneFieldsDisabled(false);
		}

		if (
			today.isAfter(dayjs(section_two_submission_date).endOf('day')) ||
			(
				!_.isEmpty(passport_asset) &&
				passport_num &&
				country_issue &&
				expiry_date
			)
		) {
			setPartTwoFieldsDisabled(true);
		} else {
			setPartTwoFieldsDisabled(false);
		}

		if (
			today.isAfter(dayjs(section_three_submission_date).endOf('day')) ||
			(
				!_.isEmpty(travel_asset) &&
				travel_arrival &&
				travel_departure
			)
		) {
			setPartThreeFieldsDisabled(true);
		} else {
			setPartThreeFieldsDisabled(false);
		}

		if (
			today.isAfter(dayjs(section_four_submission_date).endOf('day')) ||
			!_.isEmpty(insurance_asset)
		) {
			setPartFourFieldsDisabled(true);
		} else {
			setPartFourFieldsDisabled(false);
		}
	};
	//End of functions

	if (loading) {
		return (
			<div
				style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
			>
				<Spin />
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			<Row>
				<Col span={24}>
					<Form
						ref={partOneFormRef}
						layout="vertical"
						initialValues={{
							gender: personalInfoGender && personalInfoGender !== 'prefer_not_to_say' ? personalInfoGender : gender,
							last_name,
							first_name,
							phone,
							email,
							relationship: relationship_with_applicant,
							special_dietary_req: _.isNil(special_dietary_req) ? null : special_dietary_req ? 'yes' : 'no',
							special_dietary_req_detail
						}}
						onFinish={onPartOneSubmit}
					>
						<Title
							level={2}
							style={{ color: colorPrimary }}
						>Application Status</Title>
						<Row>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7
									}}
								>
									<Row><Title level={4}>{`Application ID - ${display_id ? display_id : ''}`}</Title></Row>
									<Row style={{ marginBottom: 10 }}>
										<Text
											style={{ fontSize: 18 }}
											strong
										>Application Status</Text>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: screens.xs ? 10 : 0 }}
									>
										<Col
											span={screens.xs && 12}
											style={{ width: screens.xs ? '100%' : 230 }}
										>
											<Text>Status</Text>
										</Col>
										<Col
											span={screens.xs && 12}
											style={{ width: screens.xs ? '100%' : 230 }}
											align={!screens.xs ? 'start' : undefined}
										>
											<Tooltip
												autoAdjustOverflow={true}
												placement="top"
												title={LabelHelper.applicationStatus(status)}
												key="status"
											>
												{LabelHelper.applicationStatus(status) || '-'}
											</Tooltip>
										</Col>
									</Row>
									<Row justify="center">
										<Col
											span={screens.xs && 12}
											style={{ width: screens.xs ? '100%' : 230 }}
										>
											<Text>Remarks</Text>
										</Col>
										<Col
											span={screens.xs && 12}
											style={{ width: screens.xs ? '100%' : 230 }}
											align={screens.xs === false ? 'start' : undefined}
										>
											<Tooltip
												autoAdjustOverflow={true}
												placement="top"
												title={remarks}
												key="remarks"
											>
												{remarks ? remarks : '-'}
											</Tooltip>
										</Col>
									</Row>
								</div>
							</Col>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7
									}}
								>
									<Row style={{ marginBottom: 10 }}>
										<Text
											style={{ fontSize: 18 }}
											strong
										>Recommendation Letters from Referees (Status to be updated by the Organizer)</Text>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: screens.xs ? 10 : 0 }}
									>
										<Col
											span={screens.xs && 16}
											style={{ width: screens.xs ? '100%' : 230 }}

										>
											<Text>
												First Referee’s Recommendation Letter Received
											</Text>
										</Col>
										<Col
											style={
												screens.xs ? { display: 'flex', alignItems: 'center', width: screens.xs ? '100%' : 230 }
													: { width: screens.xs ? '100%' : 230 }
											}
											span={screens.xs && 8}
											align={screens.xs === false ? 'end' : undefined}
										>
											<Text style={screens.xs && { marginLeft: 5 }}>{first_letter_received_at ? 'Yes' : 'No'}</Text>
										</Col>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: screens.xs ? 10 : 0 }}
									>
										<Col
											span={screens.xs && 16}
											style={{ width: screens.xs ? '100%' : 230 }}
										>
											<Text>
												Second Referee’s Recommendation Letter  Received
											</Text>
										</Col>
										<Col
											style={
												screens.xs ? { display: 'flex', alignItems: 'center', width: screens.xs ? '100%' : 230 }
													: { width: screens.xs ? '100%' : 230 }
											}
											span={screens.xs && 8}
											align={screens.xs === false ? 'end' : undefined}
										>
											<Text style={screens.xs && { marginLeft: 5 }}>{second_letter_received_at ? 'Yes' : 'No'}</Text>
										</Col>
									</Row>
								</div>
							</Col>
							<div style={{ marginLeft: 20 }}>
								<Text
									style={{ fontSize: 18 }}
									strong
								>
									Mandatory Information to be provided by Shortlisted Participants
								</Text>
							</div>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7,
										marginTop: 20
									}}
								>
									<Row
										justify="space-between"
										style={{ marginBottom: 10, alignItems: 'center' }}
									>
										<Col>
											<Text
												style={{ fontSize: 18 }}
												strong
											>
												Arrangement for On-campus Accommodation
											</Text>
										</Col>
										<Col>
											<Text type="secondary">
												{`Please submit by ${section_one_submission_date_string} (GMT+8)`}
											</Text>
										</Col>
									</Row>
									<Row justify="center">
										<Item
											label="Gender"
											name="gender"
											rules={[
												{
													required: true,
													message: 'Gender is required'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Select
												placeholder="Select Gender"
												options={CommonEnum.gender}
												onChange={() => {
													checkPartOneValues();
													setIsInitialLoad(false);
												}}
												disabled={((personalInfoGender && personalInfoGender !== 'prefer_not_to_say') || status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											label="Special Dietary Requirements"
											name="special_dietary_req"
											rules={[
												{
													required: true,
													message: 'Special Dietary Requirement is required'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Select
												placeholder={screens.xs ? 'Select Special Dietary...' : 'Select Special Dietary Requirement'}
												options={[
													{
														value: 'yes',
														label: 'Yes'
													},
													{
														value: 'no',
														label: 'No'
													}
												]}
												onChange={(value) => {
													checkPartOneValues();
													setIsInitialLoad(false);
													if (value === 'yes') {
														setSpecialDietReq(true);
													} else {
														setSpecialDietReq(false);
														partOneFormRef.current.setFieldsValue({ special_dietary_req_detail: null });
													}
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
											/>
										</Item>
									</Row>
									{specialDietReq && <Row justify="center">
										<Item
											label="If Yes, please state"
											name="special_dietary_req_detail"
											rules={[
												{
													required: true,
													message: 'Please state special dietary requirement details'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<TextArea
												placeholder="Enter special dietary requirements"
												onChange={() => {
													checkPartOneValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
											/>
										</Item>
									</Row>
									}
									<Row style={{ marginBottom: 10 }}>
										<Text
											style={{ fontSize: 18 }}
											strong
										>Emergency Contact Information</Text>
									</Row>
									<Row justify="center">
										<Item
											label="Family Name / Last Name"
											name="last_name"
											rules={[
												{
													required: true,
													message: 'Family Name / Last Name is required'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Input
												placeholder="Enter Family Name / Last Name"
												onChange={e => {
													checkPartOneValues();
													setIsInitialLoad(false);
													partOneFormRef.current.setFieldValue('last_name', e.target.value?.toUpperCase());
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											label="Given Name / First Name"
											name="first_name"
											rules={[
												{
													required: true,
													message: 'Given Name / First Name is required'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Input
												placeholder="Enter Given Name / First Name"
												onChange={() => {
													checkPartOneValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Col style={{ width: screens.xs ? '100%' : 460 }}>
											<Item
												label="Contact Phone No."
												name="phone"
												rules={[
													{
														required: true,
														message: 'Contact Phone No. is required'
													}
												]}
												style={{ width: screens.xs ? '100%' : 460 }}
											>
												<Input
													placeholder="Enter Contact Phone No."
													onChange={() => {
														checkPartOneValues();
														setIsInitialLoad(false);
													}}
													onKeyPress={(event) => {
														if (!/[0-9\s()+-]/.test(event.key)) {
															event.preventDefault();
															setWarningPhone(true);
															setTimeout(() => {
																setWarningPhone(false);
															}, 3000);
														} else {
															setWarningPhone(false);
														}
													}}
													onPaste={(e) => onPhoneNumberPaste(e)}
													autoComplete="nope"
													disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
												/>
											</Item>
											{warningPhone &&
												<Row style={{ marginTop: -20, marginBottom: 20 }}>
													<Text type="warning">
														Invalid character(s). Only digits, dashes, spaces, parentheses, and plus symbols are allowed.
													</Text>
												</Row>
											}
										</Col>
									</Row>
									<Row justify="center">
										<Item
											label="Email Address"
											name="email"
											rules={[
												{
													required: true,
													message: 'Email Address is required'
												},
												{ validator: validateEmail }
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Input
												placeholder="Enter Email Address"
												onChange={() => {
													checkPartOneValues();
													setIsInitialLoad(false);
												}}
												autoComplete="nope"
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											label="Relationship with Applicant"
											name="relationship"
											rules={[
												{
													required: true,
													message: 'Relationship with Applicant is required'
												}
											]}
											style={{ width: screens.xs ? '100%' : 460 }}
										>
											<Input
												placeholder="Enter Relationship with Applicant"
												onChange={() => {
													checkPartOneValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partOneFieldsDisabled}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<div style={{ marginTop: 20 }} >
										<ButtonGroupComponent
											applicationStatus={status}
											saveDisabled={submitPartOneDisabled || partOneFieldsDisabled}
											onSave={() => {
												setConfirmation(true);
												setSaveType('part_one');
											}}
											htmlType="button"
										/>
									</div>
								</div>
							</Col>
						</Row>
					</Form>
				</Col>
				{/* Part Two */}
				<Col span={24}>
					<Form
						ref={partTwoFormRef}
						layout="vertical"
						initialValues={{
							passport_number: passport_num,
							passport_country_of_issue: country_issue,
							passport_expiry_date: expiry_date ? dayjs(expiry_date) : null
						}}
						onFinish={onPartTwoSubmit}
					>
						<Row>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div style={{ marginBottom: 20 }}>
									<Text
										strong
										style={{ fontSize: 20 }}
									>
										Identity and Arrangement for Cash Cheque
									</Text>
								</div>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7
									}}
								>
									<Row
										justify="space-between"
										style={{ marginBottom: 10 }}
									>
										<Col>
											<Text
												style={{ fontSize: 18 }}
												strong
											>Travel Document</Text>
										</Col>
										<Col>
											<Text type="secondary">
												{`Please submit by ${section_two_submission_date_string} (GMT+8)`}
											</Text>
										</Col>
									</Row>
									<Row justify="center">
										<Item
											label="Passport / Exit-Entry Permit (EEP) Number (first 5 numbers including letters)"
											name="passport_number"
											style={{ width: screens.xs ? '100%' : 460 }}
											rules={[
												{
													required: true,
													message: 'Passport / EEP Number is required'
												}
											]}
										>
											<Input
												placeholder="XXXXX"
												onChange={() => {
													checkPartTwoValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partTwoFieldsDisabled}
												maxLength={5}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											label="Country of Issue"
											name="passport_country_of_issue"
											style={{ width: screens.xs ? '100%' : 460 }}
											rules={[
												{
													required: true,
													message: 'Country of Issue is required'
												}
											]}
										>
											<Select
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partTwoFieldsDisabled}
												placeholder={'Select Country of Issue'}
												options={Countries.map((country, index) => ({
													value: country.value,
													label: renderOptionLabel(country.label)
												}))}
												showSearch
												filterOption={filterOption}
												allowClear
												onChange={() => {
													checkPartTwoValues();
													setIsInitialLoad(false);
												}}
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											label="Date of Expiry"
											name="passport_expiry_date"
											style={{ width: screens.xs ? '100%' : 460 }}
											rules={[
												{
													required: true,
													message: 'Date of Expiry is required'
												}
											]}
										>
											<DatePicker
												placeholder="Select Date of Expiry"
												onChange={() => {
													checkPartTwoValues();
													setIsInitialLoad(false);
												}}
												format="YYYY-MM-DD"
												style={{ width: '100%' }}
												inputReadOnly
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partTwoFieldsDisabled}
											/>
										</Item>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: 10 }}
									>
										<Col style={{ maxWidth: 460 }}>
											<Item
												label="Passport / Exit-Entry Permit (EEP) (Max 2 files, Max 5MB each)"
												name="passport_files"
												required
												help={passportIsRequired && _.isEmpty(passportFiles) ? 'Passport is required' : ''}
												validateStatus={passportIsRequired && _.isEmpty(passportFiles) ? 'error' : ''}
											>
												<div
													style={{
														minWidth: screens.xs ? '100%' : 460,
														maxWidth: '100%',
														padding: 25,
														border: '1px solid',
														borderColor: passportIsRequired && _.isEmpty(passportFiles) ? 'red' : '#D9D9D9',
														borderRadius: 7
													}}
												>
													<Dragger
														name="file"
														accept=".jpg, .pdf"
														action={assetActionUrl}
														headers={assetHeader}
														fileList={passportFiles}
														multiple={true}
														itemRender={(originNode, file, fileList) => {
															return (
																<CustomItemRender
																	file={file}
																	assets={passport_asset}
																	onDelete={() => {
																		const filteredPassportFiles = passportFiles.filter(passportFile => passportFile.asset_id !== file.asset_id);
																		setPassportFiles(filteredPassportFiles);
																		setIsInitialLoad(false);
																	}}
																/>
															);
														}}
														beforeUpload={(file, fileList) => {
															// Check if the file size
															if (file.size > AssetHelper.getMaxSize()) {
																messageApi.open({
																	type: 'error',
																	content: `${file.name} ${AssetHelper.getMaxSizeMsg()}`,
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}
															const maxNumOfFilesExceeded = !_.isEmpty(passportFiles) ? (passportFiles.length >= 2 || passportFiles.length + fileList.length > 2) : fileList.length > 2;
															if (maxNumOfFilesExceeded) {
																messageApi.open({
																	type: 'error',
																	content: 'You can only upload up to two files.',
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}

															const fileExtension = file && file.name && file.name.split('.').pop().toLowerCase();
															const customFileName = personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Passport.${fileExtension}` : null;
															const fileName = customFileName ? `${file.name}&dl_file_name=${customFileName}` : file.name;
															const newFile = new File([file], fileName, { type: file.type });
															return newFile;
														}}
														onChange={info => {
															const { status } = info.file;
															const fileName = info.file.name.split('&dl_file_name=')[0] || '';
															if (status === 'done') {
																messageApi.open({
																	type: 'success',
																	content: `${fileName} uploaded successfully.`,
																	duration: 2.5
																});
															} else if (status === 'error') {
																messageApi.open({
																	type: 'error',
																	content: `${fileName} upload failed.`,
																	duration: 2.5
																});
															}

															let fileList = [...info.fileList];
															fileList = fileList.map(file => {
																if (file.response) {
																	file.url = AssetHelper.getDownloadUrl(file.response.data.asset._id);
																	file.asset_id = file.response.data.asset._id;
																	file.name = file.response.data.asset.file_name;
																	file.created_at = file.response.data.asset.created_at;
																}
																return file;
															});

															setPassportFiles(fileList.filter(file => file.status !== 'error'));
															setIsInitialLoad(false);
														}}
														style={{ width: '100%', padding: 10, marginBottom: 10 }}
														disabled={!_.isEmpty(passport_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partTwoFieldsDisabled}
													>
														{!_.isEmpty(passport_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partTwoFieldsDisabled ? (
															<InboxOutlined style={{ fontSize: '5em', color: '#D9D9D9' }} />
														) : (
															<>
																<InboxOutlined style={{ fontSize: '5em', color: colorLink }} />
																<p style={{ margin: 0, fontSize: 14 }}>Click or drag file to this area to upload</p>
																<p style={{ margin: 0, fontSize: 10, color: '#6B6B6B' }}>
																	Supports single or bulk upload. (.pdf or .jpg)
																</p>
															</>
														)}
													</Dragger>
												</div>
											</Item>
										</Col>
									</Row>
									<div style={{ marginTop: 20 }}>
										<ButtonGroupComponent
											applicationStatus={status}
											saveDisabled={submitPartTwoDisabled || partTwoFieldsDisabled}
											onSave={() => {
												setConfirmation(true);
												setSaveType('part_two');
											}}
											htmlType="button"
										/>
									</div>
								</div>
							</Col>
						</Row>
					</Form>
				</Col>
				{/* Part Three */}
				<Col span={24}>
					<Form
						ref={partThreeFormRef}
						layout="vertical"
						initialValues={{
							travel_arrival,
							travel_departure
						}}
						onFinish={onPartThreeSubmit}
					>
						<Row>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7
									}}
								>
									<Row
										justify="space-between"
										style={{ marginBottom: 10 }}
									>
										<Col>
											<Text
												style={{ fontSize: 18 }}
												strong
											>Travel Itinerary</Text>
										</Col>
										<Col>
											<Text type="secondary">
												{`Please submit by ${section_three_submission_date_string} (GMT+8)`}
											</Text>
										</Col>
									</Row>
									<Row justify="center">
										<Item
											style={{ width: screens.xs ? '100%' : 460 }}
											label="Arrival at HKG (Please provide the flight/train number, arrival date and time)"
											name="travel_arrival"
											rules={[
												{
													required: require_visa === 'no',
													message: 'Arrival at HKG is required'
												}
											]}
										>
											<Input
												placeholder="Flight or Train number / Date / Time"
												style={{ width: '100%' }}
												onChange={() => {
													checkPartThreeValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partThreeFieldsDisabled}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<Row justify="center">
										<Item
											style={{ width: screens.xs ? '100%' : 460 }}
											label="Departure from HKG (Please provide the flight/train number, departure date and time)"
											name="travel_departure"
											rules={[
												{
													required: require_visa === 'no',
													message: 'Departure from HKG is required'
												}
											]}
										>
											<Input
												placeholder="Flight or Train number / Date / Time"
												style={{ width: '100%' }}
												onChange={() => {
													checkPartThreeValues();
													setIsInitialLoad(false);
												}}
												disabled={(status !== 'offer_issued' && status !== 'accepted_offer') || partThreeFieldsDisabled}
												autoComplete="nope"
											/>
										</Item>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: 10 }}
									>
										<Col style={{ maxWidth: 460 }}>
											<Item
												label="Travel Itinerary & Receipt for Flight Ticket Purchase / Quotation for High Speed Rail (Hong Kong Section) Tickets (Max 5 files, Max 5MB each)"
												name="travel_files"
												required={require_visa === 'no'}
												help={travelItineraryIsRequired && _.isEmpty(travelFiles) ? 'Travel Itinerary is required' : ''}
												validateStatus={travelItineraryIsRequired && _.isEmpty(travelFiles) ? 'error' : ''}
											>
												<div
													style={{
														justifySelf: 'center',
														minWidth: screens.xs ? '100%' : 460,
														maxWidth: '100%',
														padding: 25,
														border: '1px solid',
														borderColor: travelItineraryIsRequired && _.isEmpty(travelFiles) ? 'red' : '#D9D9D9',
														borderRadius: 7
													}}
												>
													<Dragger
														name="file"
														accept=".pdf, .jpg, .jpeg"
														action={assetActionUrl}
														headers={assetHeader}
														fileList={travelFiles}
														multiple={true}
														itemRender={(originNode, file, fileList) => {
															return (
																<CustomItemRender
																	file={file}
																	assets={travel_asset}
																	onDelete={() => {
																		const filteredTravelFiles = travelFiles.filter(travelFile => travelFile.asset_id !== file.asset_id);
																		setTravelFiles(filteredTravelFiles);
																		setIsInitialLoad(false);
																	}}
																/>
															);
														}}
														beforeUpload={(file, fileList) => {
															// Check if the file size
															if (file.size > AssetHelper.getMaxSize()) {
																messageApi.open({
																	type: 'error',
																	content: `${file.name} ${AssetHelper.getMaxSizeMsg()}`,
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}
															const maxNumOfFilesExceeded = !_.isEmpty(travelFiles) ? (travelFiles.length >= 5 || travelFiles.length + fileList.length > 5) : fileList.length > 5;
															if (maxNumOfFilesExceeded) {
																messageApi.open({
																	type: 'error',
																	content: 'You can only upload up to five files.',
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}
															const fileExtension = file && file.name && file.name.split('.').pop().toLowerCase();
															const customFileName = personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Travel.${fileExtension}` : null;
															const fileName = customFileName ? `${file.name}&dl_file_name=${customFileName}` : file.name;
															const newFile = new File([file], fileName, { type: file.type });
															return newFile;
														}}
														onChange={info => {
															const { status } = info.file;
															const fileName = info.file.name.split('&dl_file_name=')[0] || '';
															if (status === 'done') {
																messageApi.open({
																	type: 'success',
																	content: `${fileName} uploaded successfully.`,
																	duration: 2.5
																});
															} else if (status === 'error') {
																messageApi.open({
																	type: 'error',
																	content: `${fileName} upload failed.`,
																	duration: 2.5
																});
															}

															let fileList = [...info.fileList];
															fileList = fileList.map(file => {
																if (file.response) {
																	file.url = AssetHelper.getDownloadUrl(file.response.data.asset._id);
																	file.asset_id = file.response.data.asset._id;
																	file.name = file.response.data.asset.file_name;
																	file.created_at = file.response.data.asset.created_at;
																}
																return file;
															});

															setTravelFiles(fileList.filter(file => file.status !== 'error'));
															setIsInitialLoad(false);
														}}
														style={{ width: '100%', padding: 10, marginBottom: 10 }}
														disabled={!_.isEmpty(travel_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partThreeFieldsDisabled}
													>
														{!_.isEmpty(travel_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partThreeFieldsDisabled ?
															(
																<InboxOutlined style={{ fontSize: '5em', color: '#D9D9D9' }} />
															) : (
																<>
																	<InboxOutlined style={{ fontSize: '5em', color: colorLink }} />
																	<p style={{ margin: 0, fontSize: 14 }}>Click or drag file to this area to upload</p>
																	<p style={{ margin: 0, fontSize: 10, color: '#6B6B6B' }}>
																		Supports single or bulk upload. (.pdf or .jpg)
																	</p>
																</>
															)}
													</Dragger>
												</div>
											</Item>
										</Col>
									</Row>
									<Row justify="center">
										<Col
											span={screens.xs && 14}
											style={{ marginBottom: screens.xs ? 10 : 0, width: screens.xs ? '100%' : 230 }}
										>
											<Text>Passport / EEP, Travel Itinerary & Receipt for Flight Ticket Purchase / Quotation for High Speed Rail (Hong Kong Section) Tickets Received</Text>
										</Col>
										<Col
											span={screens.xs && 10}
											style={{ width: screens.xs ? '100%' : 230 }}
											align={screens.xs === false ? 'end' : undefined}
										>
											<Text style={screens.xs && { marginLeft: 5 }}>{passport_received_at ? 'Yes' : 'No'}</Text>
										</Col>
									</Row>
									<Row
										style={{ marginTop: 10 }}
										justify="center"
									>
										<Col
											span={screens.xs && 14}
											style={{ width: screens.xs ? '100%' : 230 }}
										>
											<Text>Review Status</Text>
										</Col>
										<Col
											span={screens.xs && 10}

											style={{ width: screens.xs ? '100%' : 230 }}
											align={screens.xs === false ? 'end' : undefined}
										>
											<Text style={screens.xs && { marginLeft: 5 }}>{passport_review_status ? LabelHelper.passportReviewStatus(passport_review_status) : 'Pending'}</Text>
										</Col>
									</Row>
									<div style={{ marginTop: 20 }}>
										<ButtonGroupComponent
											applicationStatus={status}
											saveDisabled={submitPartThreeDisabled || partThreeFieldsDisabled}
											onSave={() => {
												setConfirmation(true);
												setSaveType('part_three');
											}}
											htmlType="button"
										/>
									</div>
								</div>
							</Col>
						</Row>
					</Form>
				</Col>
				{/* Part Four */}
				<Col span={24}>
					<Form
						ref={partFourFormRef}
						layout="vertical"
						onFinish={onPartFourSubmit}
					>
						<Row>
							<Col
								style={{ marginBottom: 20 }}
								span={24}
							>
								<div
									style={{
										minWidth: screens.xxl || screens.xl || screens.lg || screens.md ? 500 : '100%',
										maxWidth: '100%',
										padding: 25,
										border: '1px solid',
										borderColor: '#D9D9D9',
										borderRadius: 7
									}}
								>
									<Row
										justify="space-between"
										style={{ marginBottom: 10 }}
									>
										<Col>
											<Text
												style={{ fontSize: 18 }}
												strong
											>Travel Insurance Policy</Text>
										</Col>
										<Col>
											<Text type="secondary">
												{`Please submit by ${section_four_submission_date_string} (GMT+8)`}
											</Text>
										</Col>
									</Row>
									<Row
										justify="center"
										style={{ marginBottom: 10 }}
									>
										<Col>
											<Item
												label="Travel Insurance Policy (Max 2 files, Max 5MB each)"
												name="insurance_files"
												required
												help={insuranceIsRequired && _.isEmpty(insuranceFiles) ? 'Travel Itinerary is required' : ''}
												validateStatus={insuranceIsRequired && _.isEmpty(insuranceFiles) ? 'error' : ''}
											>
												<div
													style={{
														minWidth: screens.xs ? '100%' : 460,
														maxWidth: '100%',
														padding: 25,
														border: '1px solid',
														borderColor: insuranceIsRequired && _.isEmpty(insuranceFiles) ? 'red' : '#D9D9D9',
														borderRadius: 7
													}}
												>
													<Dragger
														name="file"
														accept=".pdf, .jpg, .jpeg"
														action={assetActionUrl}
														headers={assetHeader}
														fileList={insuranceFiles}
														multiple={true}
														itemRender={(originNode, file, fileList) => {
															return (
																<CustomItemRender
																	file={file}
																	assets={insurance_asset}
																	onDelete={() => {
																		const filteredInsuranceFiles = insuranceFiles.filter(insuranceFile => insuranceFile.asset_id !== file.asset_id);
																		setInsuranceFiles(filteredInsuranceFiles);
																		setIsInitialLoad(false);
																	}}
																/>
															);
														}}
														beforeUpload={(file, fileList) => {
															// Check if the file size
															if (file.size > AssetHelper.getMaxSize()) {
																messageApi.open({
																	type: 'error',
																	content: `${file.name} ${AssetHelper.getMaxSizeMsg()}`,
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}
															const maxNumOfFilesExceeded = !_.isEmpty(insuranceFiles) ? (insuranceFiles.length >= 2 || insuranceFiles.length + fileList.length > 2) : fileList.length > 2;
															if (maxNumOfFilesExceeded) {
																messageApi.open({
																	type: 'error',
																	content: 'You can only upload up to two files.',
																	duration: 2.5
																});
																file.status = 'error';
																return false;
															}

															const fileExtension = file && file.name && file.name.split('.').pop().toLowerCase();
															const customFileName = personal_info ? `${display_id}_${personal_info.last_name}_${personal_info.first_name}_Insurance.${fileExtension}` : null;
															const fileName = customFileName ? `${file.name}&dl_file_name=${customFileName}` : file.name;
															const newFile = new File([file], fileName, { type: file.type });
															return newFile;
														}}
														onChange={info => {
															const { status } = info.file;
															const fileName = info.file.name.split('&dl_file_name=')[0] || '';
															if (status === 'done') {
																messageApi.open({
																	type: 'success',
																	content: `${fileName} uploaded successfully.`,
																	duration: 2.5
																});
															} else if (status === 'error') {
																messageApi.open({
																	type: 'error',
																	content: `${fileName} upload failed.`,
																	duration: 2.5
																});
															}

															let fileList = [...info.fileList];
															fileList = fileList.map(file => {
																if (file.response) {
																	file.url = AssetHelper.getDownloadUrl(file.response.data.asset._id);
																	file.asset_id = file.response.data.asset._id;
																	file.name = file.response.data.asset.file_name;
																	file.created_at = file.response.data.asset.created_at;
																}
																return file;
															});

															setInsuranceFiles(fileList.filter(file => file.status !== 'error'));
															setIsInitialLoad(false);
														}}
														style={{ width: '100%', padding: 10, marginBottom: 10 }}
														disabled={!_.isEmpty(insurance_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partFourFieldsDisabled}
													>
														{!_.isEmpty(insurance_asset) || (status !== 'offer_issued' && status !== 'accepted_offer') || partFourFieldsDisabled ?
															(
																<InboxOutlined style={{ fontSize: '5em', color: '#D9D9D9' }} />
															) :
															(
																<>
																	<InboxOutlined style={{ fontSize: '5em', color: colorLink }} />
																	<p style={{ margin: 0, fontSize: 14 }}>Click or drag file to this area to upload</p>
																	<p style={{ margin: 0, fontSize: 10, color: '#6B6B6B' }}>
																		Supports single or bulk upload. (.pdf or .jpg)
																	</p>
																</>
															)}
													</Dragger>
												</div>
											</Item>
											<Row
												style={{ marginTop: 10 }}
												justify="center"
											>
												<Col
													span={screens.xs && 14}
													style={{ width: screens.xs ? '100%' : 230 }}
												>
													<Text>Travel Insurance Policy Received</Text>
												</Col>
												<Col
													span={screens.xs && 10}

													style={{ width: screens.xs ? '100%' : 230 }}
													align={screens.xs === false ? 'end' : undefined}
												>
													<Text style={screens.xs && { marginLeft: 5 }}>{insurance_received_at ? 'Yes' : 'No'}</Text>
												</Col>
											</Row>
											<Row
												justify="center"
											>
												<Col
													span={screens.xs && 14}
													style={{ width: screens.xs ? '100%' : 230 }}
												>
													<Text>Review Status</Text>
												</Col>
												<Col
													span={screens.xs && 10}

													style={{ width: screens.xs ? '100%' : 230 }}
													align={screens.xs === false ? 'end' : undefined}
												>
													<Text style={screens.xs && { marginLeft: 5 }}>{insurance_review_status ? LabelHelper.insuranceReviewStatus(insurance_review_status) : 'Pending'}</Text>
												</Col>
											</Row>
										</Col>
									</Row>
									<div style={{ marginTop: 20 }}>
										<ButtonGroupComponent
											applicationStatus={status}
											saveDisabled={submitPartFourDisabled || partFourFieldsDisabled}
											onSave={() => {
												setConfirmation(true);
												setSaveType('part_four');
											}}
											htmlType="button"
										/>
									</div>
								</div>
								<div>
									<ButtonGroupComponent
										applicationStatus={status}
										htmlType="button"
										isApplicationStatusPage={true}
									/>
								</div>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>
			<Confirmation
				loading={loadingSubmit || loadingPassportSubmit || loadingItinerarySubmit || loadingInsuranceSubmit}
				open={confirmation}
				onCancel={() => setConfirmation(false)}
				onOk={() => {
					if (saveType === 'part_one') {
						partOneFormRef.current.submit();
					} else if (saveType === 'part_two') {
						partTwoFormRef.current.submit();
					} else if (saveType === 'part_three') {
						partThreeFormRef.current.submit();
					} else if (saveType === 'part_four') {
						partFourFormRef.current.submit();
					}
				}}
			>
				<Flex
					vertical
					gap={30}
				>
					<>
						<Text>
							You can only submit the provided information once.
							Please review all the information carefully before proceeding.
						</Text>
						<Text>Are you sure you want to submit?</Text>
					</>
				</Flex>
			</Confirmation>
		</div>
	);
};

export default ApplicationStatusView;